import { KinderAssessment, KinderItem, KinderSection, KinderResult, ClassReportingPeriod, ClassData, Student } from '../types';
import { parseNewCard } from './kinderItemText';

/**
 * Kindergarten flashcard assessment helpers — the default letter/number/shape
 * deck, default reporting periods, and pure scoring helpers. Kept dependency-free
 * so the scoring logic is easy to reason about (and the data never muddies:
 * results are stored per period → per student → per item).
 */

const uid = (): string =>
  (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `k_${Math.random().toString(36).slice(2)}`;

const items = (values: string[], itemType: KinderItem['itemType']): KinderItem[] =>
  values.map(v => ({ id: uid(), displayValue: v, itemType }));

// Instructional-frequency order (NOT alphabetical) — must be preserved.
const UPPER = 'M S T L A K P F C N I B R J V O G W D H U Y Z Q X E'.split(' ');
const LOWER = UPPER.map(c => c.toLowerCase());
const DIGRAPHS = ['ch', 'th', 'sh', 'ph'];
// Deliberately shuffled to test recognition, not rote counting.
const NUMBERS = '8 1 6 2 4 3 5 10 7 9 12 20 14 11 16 15 13 19 18 17'.split(' ');
// Subitizing 1–10, mixed order so it tests instant recognition, not counting up.
const SUBITIZE = [2, 5, 3, 1, 4, 6, 9, 7, 10, 8];
/**
 * Numbers past twenty, IN THIS ORDER. Do not sort, shuffle or extend.
 *
 * A teacher chose this sequence deliberately and it is not arbitrary: it crosses each
 * place-value boundary a child trips on — a teens-shaped 26, the 90s, a 105 and a 120
 * either side of a hundred, a 146, and 111 where the same digit repeats. Sorting it
 * ascending would turn a recognition task into counting practice, which is the same
 * mistake the 1–20 list above is deliberately shuffled to avoid.
 */
const BEYOND_20 = [26, 97, 65, 44, 89, 30, 105, 120, 146, 111];

/**
 * HEART WORDS — high-frequency words with a part that must be learned by heart.
 *
 * The Structured Literacy distinction, not a synonym for "sight words": most of each
 * word IS decodable, and only one grapheme is irregular — the part a child marks with a
 * heart. In `said` the /s/ and /d/ are regular and the `ai` spelling of /ĕ/ is not; in
 * `was` the `a` says /ŭ/ and the `s` says /z/; in `of` the `f` says /v/.
 *
 * So this list holds words with a genuinely irregular part. Fully decodable
 * high-frequency words (`is`, `has`, `him`, `and`) are FLASH words in that framework —
 * they belong in regular decoding practice, and putting them here would teach the
 * child that a word they can sound out must instead be memorised, which is the exact
 * habit heart-word teaching exists to prevent.
 */
const HEART_WORDS = ['the', 'was', 'said', 'of', 'to', 'you', 'are', 'he', 'she', 'they', 'have', 'do'];

/**
 * CVC WORDS — consonant–vowel–consonant, three sounds, fully decodable.
 *
 * The first blending step: a child who knows the letter sounds can read every one of
 * these without being told any of them. Ordered to rotate through all five short vowels
 * rather than grouping by vowel — a column of `cat mat hat` can be read off the pattern
 * without decoding each word, which is the thing being assessed.
 *
 * It includes the CVC VERBS (`ran`, `can`, `hop`, `sit`) rather than nouns alone, because
 * the Sentence Readers below are built only from this list and the heart words: without
 * a verb here, a decodable sentence cannot be written at all. My first draft of both
 * lists missed that and the sentences used three words no section taught — a test
 * comparing the two caught it.
 */
const CVC_WORDS = [
  'cat', 'bed', 'pig', 'hot', 'sun', 'ran', 'net', 'sit', 'dog', 'cup',
  'hat', 'red', 'tin', 'top', 'bug', 'map', 'hop', 'can',
];

/**
 * SENTENCE READERS — six short decodable sentences.
 *
 * Built ONLY from the two sections above: every content word is CVC and every other
 * word is a heart word from that list. So a child who has passed those two sections can
 * read these, and a child who cannot tells you which of the two to go back to — which
 * is what makes it an assessment rather than a reading exercise.
 */
const SENTENCE_READERS = [
  'The cat ran.',
  'The dog can hop.',
  'The sun was hot.',
  'You have a bug.',
  'He can hop to the top.',
  'They have a map.',
];

// name → hex color (standard kindergarten colours)
const COLORS: Array<[string, string]> = [
  ['Red', '#dc2626'], ['Orange', '#ea580c'], ['Yellow', '#eab308'], ['Green', '#16a34a'],
  ['Blue', '#2563eb'], ['Purple', '#7c3aed'], ['Pink', '#ec4899'], ['Brown', '#92400e'],
  ['Black', '#111827'], ['White', '#ffffff'], ['Gray', '#6b7280'],
];

// name → [shapeName, hex color]
const SHAPES: Array<[string, string]> = [
  ['circle', '#2563eb'], ['square', '#16a34a'], ['triangle', '#a21caf'], ['rectangle', '#2563eb'],
  ['star', '#eab308'], ['diamond', '#ea580c'], ['oval', '#7c3aed'], ['heart', '#dc2626'],
  ['crescent', '#eab308'], ['pentagon', '#dc2626'], ['hexagon', '#16a34a'], ['octagon', '#dc2626'],
];

// Section factories (also used by the "add starter sections" upgrade).
const secUpper = (): KinderSection => ({ id: uid(), name: 'Uppercase Letters', items: items(UPPER, 'letter_upper') });
const secLower = (): KinderSection => ({ id: uid(), name: 'Lowercase Letters', items: items(LOWER, 'letter_lower') });
const secSounds = (): KinderSection => ({ id: uid(), name: 'Letter Sounds', items: items(LOWER, 'letter_sound') });
const secDigraphs = (): KinderSection => ({ id: uid(), name: 'Digraphs', items: items(DIGRAPHS, 'digraph') });
const secNumbers = (): KinderSection => ({ id: uid(), name: 'Number Recognition', items: items(NUMBERS, 'number') });
const secBeyond20 = (): KinderSection => ({ id: uid(), name: 'Number Recognition (Beyond 20)', items: items(BEYOND_20.map(String), 'number') });
/**
 * The three word-reading sections carry an explicit `domain`.
 *
 * Single words parse as `custom`, which abstains from the item-type vote in
 * domainForSection — so these would fall through to the NAME hint and land under
 * Literacy only because "Words" happens to match a regex. Renaming a section should not
 * be able to move it to Numeracy.
 */
const secHeartWords = (): KinderSection => ({ id: uid(), name: 'Heart Words', items: items(HEART_WORDS, 'custom'), domain: 'literacy' });
const secCvcWords = (): KinderSection => ({ id: uid(), name: 'CVC Words', items: items(CVC_WORDS, 'custom'), domain: 'literacy' });
const secSentences = (): KinderSection => ({ id: uid(), name: 'Sentence Readers', items: items(SENTENCE_READERS, 'phrase'), domain: 'literacy' });
const secSubitize = (): KinderSection => ({ id: uid(), name: 'Subitizing (to 10)', items: SUBITIZE.map(n => ({ id: uid(), displayValue: String(n), itemType: 'dots' as const, meta: { count: n } })) });
const secColors = (): KinderSection => ({ id: uid(), name: 'Colors', items: COLORS.map(([cName, color]) => ({ id: uid(), displayValue: cName, itemType: 'color' as const, meta: { color } })) });
const secShapes = (): KinderSection => ({ id: uid(), name: 'Shape Identification', items: SHAPES.map(([shapeName, color]) => ({ id: uid(), displayValue: shapeName.charAt(0).toUpperCase() + shapeName.slice(1), itemType: 'shape' as const, meta: { shapeName, color } })) });

/** The default flashcard deck: uppercase, lowercase, sounds, digraphs, numbers, subitizing, colors, shapes. */
export function defaultKinderDeck(name = 'Letter / Number Assessment'): KinderAssessment {
  const sections = [
    secUpper(), secLower(), secSounds(), secDigraphs(), secCvcWords(), secHeartWords(), secSentences(),
    secNumbers(), secBeyond20(), secSubitize(), secColors(), secShapes(),
  ];
  return { id: uid(), name, sections, passKey: 'q', failKey: 'z', results: {}, createdAt: new Date().toISOString() };
}

/** Starter sections not already present (by name) in a deck — for the upgrade button. */
export function missingStarterSections(existingNames: string[]): KinderSection[] {
  const have = new Set(existingNames.map(n => n.toLowerCase()));
  return [secUpper, secLower, secSounds, secDigraphs, secCvcWords, secHeartWords, secSentences,
          secNumbers, secBeyond20, secSubitize, secColors, secShapes]
    .map(f => f())
    .filter(s => !have.has(s.name.toLowerCase()));
}

// The 10 Kindergarten counting concepts, scored D/E/N (Demonstrates / Emerging /
// Not yet). Source: "Key Concepts of Counting" (K. Methot, Math Learning Coord.).
const COUNTING_CONCEPTS: Array<{ name: string; prompt: string; expectation: string; d: string; e: string; n: string; next: string; demo?: import('../types').KinderDemo }> = [
  { name: 'Stable Order', prompt: 'Administer together with 1-to-1 Correspondence (below). Listen to the count sequence.', expectation: '15.4 — stable order', d: 'Counted to 10 in order.', e: 'Counted partway in order.', n: 'Not demonstrating stable order yet.', next: 'Count to 10 using songs and chants; choral counting.', demo: { kind: 'counters', count: 10 } },
  { name: '1-to-1 Correspondence', prompt: 'Display 10 counters. Say: "Can you please count the counters?" (Ask them to touch each counter as they count if they don\'t.)', expectation: '15.3 — one-to-one correspondence', d: 'Each counter receives only one count.', e: 'Some understanding with minor errors.', n: 'Assigns two number words to one counter.', next: 'Model and practice moving each item as you count aloud.', demo: { kind: 'counters', count: 10 } },
  { name: 'Subitizing (Perceptual)', prompt: 'Use one number cube; show 4 dots for 2–3 seconds. Say: "How many dots do you see?" Repeat with 3, then 5.', expectation: '15.5 — subitize quantities to 5', d: 'Automatic recognition of all images.', e: 'Recognizes some images.', n: 'Visually counts / points / long pause / incorrect.', next: 'Practice with dot plates, dominoes, dice throughout the day.', demo: { kind: 'subitize', values: [4, 3, 5] } },
  { name: 'Magnitude', prompt: 'Piles of 8 and 5 counters ("gold coins"). Say: "Give me the pile with the most. How do you know it has more?"', expectation: '15.1 — position determines magnitude', d: 'Points to larger pile AND explains thinking.', e: 'Selects larger pile but cannot explain.', n: 'Selects smaller pile / must match counters to see.', next: 'Math talks comparing collections; use a number path toward 10.', demo: { kind: 'twoPiles', a: 8, b: 5 } },
  { name: 'Cardinality', prompt: 'Pile of 20 counters. Say: "Give me 5." Hold them up: "How many do I have?" Repeat for 3, 6, 2.', expectation: '15.2 — last number = the amount', d: 'Gives 2, 3, 5, 6 and tells amount without recounting.', e: 'Gives 2 and 3, tells amount without recounting.', n: 'Incorrect amounts / recounts the set.', next: 'Model that the last number counted tells how many.', demo: { kind: 'counters', count: 5, values: [5, 3, 6, 2] } },
  { name: 'Hierarchical Inclusion', prompt: '5 counters: "How many?" Add one: "How many now?" Remove to 5, confirm. Remove one: "How many now?"', expectation: 'One more / one less; the less is in the more', d: 'Recognizes 6 and 4 without recounting.', e: 'Recognizes one more OR one less, not both.', n: 'Recounts / wrong number.', next: 'Practice one-more and one-less situations (e.g. block towers).', demo: { kind: 'addRemove', count: 5 } },
  { name: 'Conservation', prompt: 'Two aligned rows of 5. "Count the top. Count the bottom. Same?" Spread the bottom row longer. "Still the same?"', expectation: 'SE 15.1–15.10', d: 'Recognizes both rows are still the same.', e: 'Recounts, then recognizes they are the same.', n: 'Does not recognize they are the same.', next: 'Model equal rows spread apart; explicitly teach same number, just longer.', demo: { kind: 'conservation', count: 5 } },
  { name: 'Order Irrelevance', prompt: 'Display 5 counters. Ask them to start counting from the middle counter: "How many?" Repeat right to left.', expectation: '15.4 — order irrelevance', d: 'Accurately counts to five.', e: 'Recounts.', n: 'Not demonstrating order irrelevance yet.', next: 'Count objects without starting from the first object.', demo: { kind: 'counters', count: 5 } },
  { name: 'Abstraction', prompt: '5 large + 5 small cubes (or two sets of five, e.g. bears & beans). "More large, more small, or the same? How do you know?"', expectation: 'OE 15 — same quantity can look different', d: 'Recognizes both quantities are the same.', e: 'Recounts several times, seems unsure.', n: 'Says more large/small; wrong number or can\'t explain.', next: 'Visual math talks; count a variety of similar/different sets.', demo: { kind: 'abstraction', count: 5 } },
  { name: 'Parts–Whole / Compensation', prompt: '"Show me 5 with your fingers. Now show me 5 a different way. How is that still 5?" Repeat with 3, and with 8 (Year 2) or 4 (Year 1).', expectation: '15.9 — compose/decompose to 10', d: 'Accurately composes numbers AND explains thinking.', e: 'Composes some numbers, or composes but can\'t explain.', n: 'Unable to show multiple representations.', next: 'Model flexible representations of numbers throughout the day.', demo: { kind: 'compose', values: [5, 3, 8, 4] } },
];

/** The on-screen manipulative for a concept, looked up by NAME so it also works
 *  for rubric assessments created before the demos existed (no stored meta.demo). */
export function conceptDemoByName(name: string): import('../types').KinderDemo | undefined {
  const c = COUNTING_CONCEPTS.find(x => x.name.toLowerCase() === (name || '').toLowerCase());
  return c?.demo;
}

/** The observational "Counting Concepts" rubric assessment (scored D/E/N). */
export function defaultCountingConceptsAssessment(): KinderAssessment {
  return {
    id: uid(),
    name: 'Counting Concepts (Observational)',
    kind: 'rubric',
    sections: [{
      id: uid(), name: 'Key Concepts of Counting',
      items: COUNTING_CONCEPTS.map(c => ({
        id: uid(), displayValue: c.name, itemType: 'concept' as const,
        meta: { prompt: c.prompt, expectation: c.expectation, lookForD: c.d, lookForE: c.e, lookForN: c.n, nextSteps: c.next, demo: c.demo },
      })),
    }],
    passKey: 'q', failKey: 'z', almostKey: 'w', // D / N / E — administered via buttons, not keys
    results: {}, notes: {}, createdAt: new Date().toISOString(),
  };
}

/** Default reporting periods for a new class. */
export function defaultReportingPeriods(): ClassReportingPeriod[] {
  const now = new Date().toISOString();
  return [
    { id: uid(), name: 'Beginning', createdAt: now },
    { id: uid(), name: 'Midterm', createdAt: now },
    { id: uid(), name: 'End', createdAt: now },
  ];
}

export const newKinderId = uid;

/** Every item across all sections, in order. */
export function allItems(a: KinderAssessment): KinderItem[] {
  return a.sections.flatMap(s => s.items);
}

/** The stored result for one (period, student, item), or undefined. */
export function getResult(
  a: KinderAssessment, periodId: string, studentId: string, itemId: string,
): KinderResult | undefined {
  return a.results?.[periodId]?.[studentId]?.[itemId];
}

/** Immutably set/clear a result. Passing undefined clears it. Never mutates. */
export function setResult(
  a: KinderAssessment, periodId: string, studentId: string, itemId: string, value: KinderResult | undefined,
): KinderAssessment {
  const results = { ...(a.results || {}) };
  const byStudent = { ...(results[periodId] || {}) };
  const byItem = { ...(byStudent[studentId] || {}) };
  if (value === undefined) delete byItem[itemId]; else byItem[itemId] = value;
  byStudent[studentId] = byItem;
  results[periodId] = byStudent;
  return { ...a, results };
}

/** One cell to clear: a student's mark on one item. */
export interface KinderCell { studentId: string; itemId: string }

/**
 * Immutably clear a set of cells in one period.
 *
 * Takes explicit cells rather than "clear this student" or "clear everything"
 * because the grid is drawn PER SECTION: a row is that student's marks on this
 * section's items, not their whole record. A teacher clearing a row in
 * Uppercase Letters must not lose the Numbers marks they cannot see from there.
 *
 * Empty objects are pruned so an emptied period does not leave
 * `results[period][student] = {}` behind, which reads as "assessed, no marks"
 * to anything counting keys.
 */
export function clearResults(
  a: KinderAssessment, periodId: string, cells: KinderCell[],
): KinderAssessment {
  if (!cells.length) return a;
  const results = { ...(a.results || {}) };
  const byStudent = { ...(results[periodId] || {}) };
  const touched = new Set(cells.map(c => c.studentId));
  for (const studentId of touched) {
    const byItem = { ...(byStudent[studentId] || {}) };
    for (const c of cells) if (c.studentId === studentId) delete byItem[c.itemId];
    if (Object.keys(byItem).length) byStudent[studentId] = byItem;
    else delete byStudent[studentId];
  }
  if (Object.keys(byStudent).length) results[periodId] = byStudent;
  else delete results[periodId];
  return { ...a, results };
}

/** Teacher note for one (period, student, item), or ''. */
export function getNote(a: KinderAssessment, periodId: string, studentId: string, itemId: string): string {
  return a.notes?.[periodId]?.[studentId]?.[itemId] || '';
}
/** Immutably set/clear a note. Never mutates. */
export function setNote(a: KinderAssessment, periodId: string, studentId: string, itemId: string, text: string): KinderAssessment {
  const notes = { ...(a.notes || {}) };
  const byStudent = { ...(notes[periodId] || {}) };
  const byItem = { ...(byStudent[studentId] || {}) };
  if (text.trim()) byItem[itemId] = text; else delete byItem[itemId];
  byStudent[studentId] = byItem;
  notes[periodId] = byStudent;
  return { ...a, notes };
}

/** One observation note, with everything needed to show it out of context. */
export interface KinderNoteEntry {
  periodId: string;
  studentId: string;
  itemId: string;
  text: string;
  /** The card the note is about ("M", "ch", "8 ducks"), or '' if since deleted. */
  itemLabel: string;
  /** The section the card sits in, for grouping. */
  sectionName: string;
  /** The mark recorded on that card in that period, if any. */
  result?: KinderResult;
}

/**
 * Every observation note on this assessment, newest period last.
 *
 * Flat rather than nested because every way a teacher wants to read these is a
 * different grouping of the same list — all of a child's notes, everything
 * written about one letter, or just this testing period — and a flat list with
 * the labels already resolved can be grouped any of those ways at the point of
 * display without re-walking the tree.
 *
 * Notes for items or students that no longer exist are KEPT, with an empty
 * itemLabel. A teacher's own written observation is not ours to hide because the
 * card it was attached to was later renamed or removed; dropping it silently
 * would lose work they cannot get back.
 */
export function collectNotes(
  a: KinderAssessment,
  opts: { periodId?: string } = {},
): KinderNoteEntry[] {
  const labelOf = new Map<string, { label: string; section: string }>();
  for (const sec of a.sections) {
    for (const it of sec.items) {
      labelOf.set(it.id, {
        label: it.itemType === 'shape' ? (it.meta?.shapeName || it.displayValue) : it.displayValue,
        section: sec.name,
      });
    }
  }
  const out: KinderNoteEntry[] = [];
  for (const [periodId, byStudent] of Object.entries(a.notes || {})) {
    if (opts.periodId && periodId !== opts.periodId) continue;
    for (const [studentId, byItem] of Object.entries(byStudent || {})) {
      for (const [itemId, text] of Object.entries(byItem || {})) {
        if (!text || !text.trim()) continue;
        const hit = labelOf.get(itemId);
        out.push({
          periodId, studentId, itemId, text,
          itemLabel: hit?.label || '',
          sectionName: hit?.section || '',
          result: getResult(a, periodId, studentId, itemId),
        });
      }
    }
  }
  return out;
}

/** How many notes exist, optionally within one period. */
export const countNotes = (a: KinderAssessment, periodId?: string): number =>
  collectNotes(a, periodId ? { periodId } : {}).length;

export interface SectionSubtotal {
  pass: number; almost: number; fail: number; assessed: number; total: number;
}

/**
 * A ready-made practice kindergarten class so a teacher can explore the tool
 * without building their own — sample students, the full default deck, three
 * periods, and pre-filled uppercase results that visibly improve over time
 * (so the Progression tab has something to show).
 */
export function buildPracticeKinderClass(): Partial<ClassData> {
  const deck = defaultKinderDeck('Letter / Number Assessment');
  deck.almostKey = 'w'; // show the optional 3rd state in the demo
  const periods = defaultReportingPeriods();
  const students: Student[] = [
    { firstName: 'Ava', lastName: 'M.' },
    { firstName: 'Liam', lastName: 'K.' },
    { firstName: 'Noah', lastName: 'S.' },
    { firstName: 'Mia', lastName: 'R.' },
  ].map((s, i) => ({ id: `demo_${i}_${uid()}`, studentNumber: `D${i + 1}`, firstName: s.firstName, lastName: s.lastName }));

  const upper = deck.sections[0];
  const results: NonNullable<KinderAssessment['results']> = {};
  periods.forEach((p, pIdx) => {
    const byStudent: Record<string, Record<string, KinderResult>> = {};
    students.forEach((st, sIdx) => {
      const byItem: Record<string, KinderResult> = {};
      const known = Math.min(upper.items.length, 6 + pIdx * 7 + sIdx * 2); // grows each period
      upper.items.forEach((it, iIdx) => {
        byItem[it.id] = iIdx < known ? 'pass' : iIdx < known + 2 ? 'almost' : 'fail';
      });
      byStudent[st.id] = byItem;
    });
    results[p.id] = byStudent;
  });
  deck.results = results;

  return {
    name: 'Practice · Kindergarten Demo',
    code: 'DEMO-K',
    semester: '1',
    level: 'kindergarten',
    students,
    kinderAssessments: [deck, defaultCountingConceptsAssessment()],
    reportingPeriods: periods,
    assessments: [],
    grades: {},
    attendance: [],
    stream: [],
    themeColor: '#7c3aed',
  };
}

/** Per-section tally for one student in one period. */
/**
 * Has anyone been marked "almost" on this deck?
 *
 * Display of the yellow state follows the DATA, not the key setting. A deck
 * whose almost key is unbound can still hold almost cells from before, and
 * hiding the legend leaves yellow squares on screen with nothing explaining
 * them — the same class of bug that was hiding those marks on the printed
 * report entirely.
 */
export function anyAlmostRecorded(a: KinderAssessment | undefined | null): boolean {
  const periods = a?.results;
  if (!periods) return false;
  for (const byStudent of Object.values(periods)) {
    for (const byItem of Object.values(byStudent || {})) {
      for (const r of Object.values(byItem || {})) {
        if (r === 'almost') return true;
      }
    }
  }
  return false;
}

export function sectionSubtotal(
  a: KinderAssessment, section: KinderSection, periodId: string, studentId: string,
): SectionSubtotal {
  const sub: SectionSubtotal = { pass: 0, almost: 0, fail: 0, assessed: 0, total: section.items.length };
  for (const item of section.items) {
    const r = getResult(a, periodId, studentId, item.id);
    if (r === 'pass') { sub.pass++; sub.assessed++; }
    else if (r === 'almost') { sub.almost++; sub.assessed++; }
    else if (r === 'fail') { sub.fail++; sub.assessed++; }
  }
  return sub;
}

/**
 * A section from a name and a list of typed items.
 *
 * Pure, and separate from the modal that collects them, so the two things worth being
 * sure about can be tested without mounting anything: that the items keep the ORDER
 * they were entered in, and that each one goes through the same `parseNewCard` the
 * inline add-a-card box uses. A teacher who types "8 🦆" into a new section should get
 * the counting card they would have got typing it anywhere else.
 *
 * Unparseable entries (blank, whitespace) are dropped rather than becoming empty cards.
 */
export function buildKinderSection(
  name: string,
  rawItems: readonly string[],
  domain?: KinderSection['domain'],
): KinderSection {
  const parsed = rawItems
    .map(raw => ({ raw, card: parseNewCard(raw) }))
    .filter((x): x is { raw: string; card: NonNullable<ReturnType<typeof parseNewCard>> } => x.card !== null);
  return {
    id: uid(),
    name: name.trim() || 'New Section',
    items: parsed.map(({ card }) => ({
      id: uid(),
      displayValue: card.displayValue,
      itemType: card.itemType,
      ...(card.meta ? { meta: card.meta } : {}),
    })),
    ...(domain ? { domain } : {}),
  };
}

/**
 * The LETTER SOUNDS card matching a letter-name card, or null.
 *
 * A kindergarten teacher showing "M" asks two things in one breath: what letter is this, and
 * what sound does it make. They are separate sections of the deck because they are separate
 * skills — a child can name every letter and sound none of them — but they are assessed in the
 * same moment, and before this the teacher had to run the whole alphabet twice to record it.
 *
 * So the slideshow offers the sound alongside the letter, and this is the lookup that makes it
 * possible: the sound lives on a DIFFERENT section's item, found by letter.
 *
 * Only for the two letter-NAME types. A digraph card ("ch") has no single-letter sound item, and
 * a number or a shape obviously has none — returning something for those would record a mark
 * against whatever item happened to start with the same character.
 */
export function soundItemFor(
  assessment: Pick<KinderAssessment, 'sections'> | null | undefined,
  card: Pick<KinderItem, 'displayValue' | 'itemType'> | null | undefined,
): KinderItem | null {
  if (!assessment || !card) return null;
  if (card.itemType !== 'letter_upper' && card.itemType !== 'letter_lower') return null;
  const letter = (card.displayValue || '').trim().toLowerCase().charAt(0);
  // A letter, not a digit or a stray glyph: `parseScore`-style defensiveness, because a deck can
  // hold whatever a teacher typed.
  if (!letter || !/[a-z]/.test(letter)) return null;
  for (const sec of assessment.sections || []) {
    for (const it of sec.items || []) {
      if (it.itemType !== 'letter_sound') continue;
      // First match wins. A teacher who duplicated the sounds section has two items for the same
      // letter; marking one of them is right, and marking both would be worse.
      if ((it.displayValue || '').trim().toLowerCase().charAt(0) === letter) return it;
    }
  }
  return null;
}
